/**
 * Platform Members Tab — Analytics Dashboard + Full User Management
 * Wraps analytics (health banner, KPIs, charts) above existing UserAdminTab functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  Menu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Alert,
  Skeleton,
  Switch,
  FormControlLabel,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Visibility as ViewIcon,
  VpnKey as PasswordIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Cancel as CancelIcon,
  PlayArrow as ActivateIcon,
  MonetizationOn as MonetizationIcon,
  People as PeopleIcon,
  AutoGraph as AutoGraphIcon,
  ContentCopy as ContentCopyIcon,
  PersonAdd as PersonAddIcon,
  Security as VaultIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Area,
  AreaChart,
  BarChart,
  Bar,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import { apiService } from '../../services/api';
import JoinReasonByAgeChart from './JoinReasonByAgeChart';
import MemberHealthBanner from './components/MemberHealthBanner';
import AlertBanner from './components/AlertBanner';
import KpiCardGrid from './components/KpiCardGrid';
import ConversionFunnel from './components/ConversionFunnel';
import CreatePlatformMemberModal from './CreatePlatformMemberModal';
import MemberLocation, { LocationSource, LocationConfidence, flagEmoji, countryName } from '../common/MemberLocation';
import {
  FALLBACK_SUBSCRIPTION_PLANS,
  SubscriptionPlan,
  displayNameFor,
  TIER_COLORS,
} from '../../config/subscriptionPlans';

const PLAN_COLORS: Record<string, string> = {
  free: '#94a3b8',
  trial: '#60a5fa',
  basic_monthly: '#34d399',
  basic_annual: '#059669',
  standard_monthly: '#a78bfa',
  standard_annual: '#7c3aed',
  unlimited_monthly: '#D4AC0D',
  unlimited_annual: '#b8860b',
  unlimited_yearly: '#b8860b',
  premium_monthly: '#f97316',
  premium_annual: '#ea580c',
  legacy_monthly: '#dc2626',
  legacy_annual: '#b91c1c',
  monthly_unlimited: '#D4AC0D',
  annual_unlimited: '#b8860b',
  lifetime_unlimited: '#dc2626',
};

interface PlatformUser {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone_number?: string;
  phone_display: string;
  phone_verified: boolean;
  phone_verified_display: string;
  /** Free-text the MEMBER typed about themselves. Not the derived location. */
  location?: string;
  /** Derived location — resolved from network/commercial signals. See MemberLocation.tsx. */
  profile_location?: string | null;
  location_country_code?: string | null;
  location_region?: string | null;
  location_city?: string | null;
  location_source?: LocationSource;
  location_confidence?: LocationConfidence;
  location_resolved_at?: string | null;
  account_class?: string;
  age_range?: string;
  subscription_plan: string;
  subscription_status: string;
  subscription_display_text: string;
  is_trial_user: boolean;
  user_status: string;
  chapter_credits: number;
  has_unlimited_credits: boolean;
  chapters_created: number;
  chapters_completed: number;
  total_quests_completed: number;
  current_level: number;
  current_quest: number;
  progress_display: string;
  quest_level_display: string;
  quest_progress: string;
  total_styles_purchased: number;
  purchased_writing_styles: string[];
  purchased_illustration_styles: string[];
  styles_purchased: {
    writing: string[];
    illustration: string[];
    total: number;
  };
  profile_picture?: string;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  family_vault_tier?: 'standard' | 'premium' | null;
  family_vault_status?: string | null;
  family_vault_current_period_end?: string | null;
  [key: string]: any;
}

interface SearchFilters {
  search: string;
  subscription_plan: string;
  chapter_credits: string;
  styles_purchased: string;
  age_range: string;
  family_vault_tier: string;
  /** Multi-select country codes; empty = no country filter. */
  location_country: string[];
  location_confidence: string;
  account_class: string;
}

interface PlatformStats {
  health: {
    total_members: number;
    active_subscribers: number;
    premium_members: number;
    legacy_creator_members: number;
    verified_members: number;
    new_this_month: number;
    new_this_week: number;
    vault_subscribers: number;
    free_members: number;
    trial_members: number;
    basic_members: number;
  };
  arpu: number;
  plan_distribution: { plan: string; count: number }[];
  member_growth: { date: string; count: number }[];
  content_stats: { avg_chapters: number; total_chapters: number; members_with_chapters: number };
  quest_stats: { members_with_quests: number; total_responses: number };
  at_risk_count: number;
}

export const PlatformMembersTab: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { user: currentUser } = useSelector((state: RootState) => state.auth);

  // Analytics state
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // User table state (preserved from UserAdminTab)
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [userStats, setUserStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filters, setFilters] = useState<SearchFilters>({
    search: '',
    subscription_plan: '',
    chapter_credits: '',
    styles_purchased: '',
    age_range: '',
    family_vault_tier: '',
    location_country: [],
    location_confidence: '',
    // Test candidates and quarantined accounts stay out of the default view.
    account_class: 'member',
  });
  const [availableCountries, setAvailableCountries] = useState<Array<{
    country_code: string;
    member_count: number;
    high_confidence: number;
    medium_confidence: number;
    low_confidence: number;
  }>>([]);
  const [createMemberDialogOpen, setCreateMemberDialogOpen] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>(FALLBACK_SUBSCRIPTION_PLANS);
  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null);

  // Dialog states (preserved from UserAdminTab)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PlatformUser | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<PlatformUser>>({});
  const [subscriptionFormData, setSubscriptionFormData] = useState({
    plan_type: 'standard_monthly',
    credits: 0,
    creditOperation: 'set'
  });
  const [cancelFormData, setCancelFormData] = useState({
    immediate: false,
    reason: 'Admin cancellation',
    credits: 0,
    creditOperation: 'set'
  });
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [bulkActionMenuAnchor, setBulkActionMenuAnchor] = useState<null | HTMLElement>(null);

  // Load analytics
  const loadAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const data = await apiService.getPlatformMemberStats();
      setStats(data);
    } catch (error) {
      console.warn('Failed to load platform analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  // Load users (preserved from UserAdminTab)
  const loadUsers = useCallback(async (resetPage = false) => {
    try {
      setLoading(resetPage);
      const currentPage = resetPage ? 0 : page;
      const { location_country, ...restFilters } = filters;
      const params = {
        page: currentPage + 1,
        limit: rowsPerPage,
        ...restFilters,
        // Serialised explicitly: the generic query builder would stringify the
        // array by accident, which is fragile to depend on.
        location_country: location_country.length ? location_country.join(',') : '',
      };
      const response = await apiService.getPlatformUsers(params);
      setUsers(response.users || []);
      setTotalUsers(response.pagination?.total || 0);
      const statsResponse = await apiService.getPlatformUserStats();
      setUserStats(statsResponse.overview || null);
      if (resetPage) setPage(0);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load users',
      }));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, rowsPerPage, filters, dispatch]);

  // All handlers preserved from UserAdminTab
  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    if (searchDebounce) clearTimeout(searchDebounce);
    const timeout = setTimeout(() => { loadUsers(true); }, 500);
    setSearchDebounce(timeout);
  };

  const handleFilterChange = (key: keyof SearchFilters, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    loadUsers(true);
  };

  const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedUsers(e.target.checked ? users.map(u => u.id) : []);
  };
  const handleSelectUser = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleViewUser = (user: PlatformUser) => { setSelectedUser(user); setViewDialogOpen(true); };
  const handleEditUser = (user: PlatformUser) => {
    setSelectedUser(user);
    setEditFormData({
      ...user,
      // Seed vault selectors from the JOIN result on /platform-users
      vault_subscription_tier: user.family_vault_tier || '',
      vault_subscription_status: user.family_vault_status || '',
      vault_subscription_cycle: 'annual',
    } as any);
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser || !editFormData) return;
    try {
      // 1) Sync the main user row
      await apiService.updatePlatformUser(selectedUser.id, editFormData);

      // 2) Sync Family Vault via the dedicated endpoint (JOIN-backed value)
      const prior = selectedUser.family_vault_tier || '';
      const next = (editFormData as any).vault_subscription_tier || '';
      const cycle = ((editFormData as any).vault_subscription_cycle || 'annual') as 'monthly' | 'annual';
      const userIdStr = String(selectedUser.id);
      if (next && next !== prior) {
        await apiService.grantFamilyVault(userIdStr, {
          tier: next as 'standard' | 'premium',
          cycle,
          reason: 'Admin edit — Family Vault tier changed from dashboard',
        });
      } else if (!next && prior) {
        await apiService.revokeFamilyVault(userIdStr, 'Admin edit — Family Vault removed from dashboard');
      }

      dispatch(addNotification({ type: 'success', message: 'User updated successfully' }));
      setEditDialogOpen(false);
      loadUsers();
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: error instanceof Error ? error.message : 'Failed to update user' }));
    }
  };

  const handleResetPassword = async (userId: number) => {
    try {
      await apiService.resetPlatformUserPassword(userId);
      dispatch(addNotification({ type: 'success', message: 'Password reset email sent' }));
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: error instanceof Error ? error.message : 'Failed to reset password' }));
    }
  };

  const handleActivateSubscription = (user: PlatformUser) => {
    setSelectedUser(user);
    setSubscriptionFormData({
      plan_type: user.subscription_plan || 'standard_monthly',
      credits: 0,
      creditOperation: 'set',
    });
    setSubscriptionDialogOpen(true);
  };

  const handleCancelSubscription = (user: PlatformUser) => {
    setSelectedUser(user);
    setCancelFormData({ immediate: true, reason: 'Admin deactivation', credits: 0, creditOperation: 'set' });
    setCancelSubscriptionDialogOpen(true);
  };

  const executeActivateSubscription = async () => {
    if (!selectedUser) return;
    try {
      const updates: string[] = [];
      if (subscriptionFormData.plan_type !== selectedUser.subscription_plan) {
        // V4.3: use admin plan-change endpoint that actually writes to users.subscription_plan.
        await apiService.updateUserSubscriptionPlan(String(selectedUser.id), {
          new_tier: subscriptionFormData.plan_type,
          reason: 'Admin-initiated plan change from dashboard',
          comp_grant: true,
        });
        updates.push(`plan updated to ${displayNameFor(subscriptionFormData.plan_type, subscriptionPlans)}`);
      }
      if (subscriptionFormData.credits > 0) {
        await apiService.updateUserCredits(selectedUser.id, {
          credits: subscriptionFormData.credits,
          operation: subscriptionFormData.creditOperation as 'set' | 'add' | 'subtract'
        });
        updates.push(`credits ${subscriptionFormData.creditOperation === 'set' ? 'set to' : subscriptionFormData.creditOperation === 'add' ? 'increased by' : 'decreased by'} ${subscriptionFormData.credits}`);
      }
      dispatch(addNotification({
        type: updates.length > 0 ? 'success' : 'info',
        message: updates.length > 0 ? `Updated ${selectedUser.full_name}: ${updates.join(', ')}` : `No changes made`,
      }));
      setSubscriptionDialogOpen(false);
      loadUsers();
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: error instanceof Error ? error.message : 'Failed to update subscription' }));
    }
  };

  const executeCancelSubscription = async () => {
    if (!selectedUser) return;
    try {
      await apiService.cancelUserSubscription(selectedUser.id, { immediate: cancelFormData.immediate, reason: cancelFormData.reason });
      if (cancelFormData.credits > 0) {
        await apiService.updateUserCredits(selectedUser.id, {
          credits: cancelFormData.credits,
          operation: cancelFormData.creditOperation as 'set' | 'add' | 'subtract'
        });
      }
      dispatch(addNotification({ type: 'success', message: `Subscription updated for ${selectedUser.full_name}` }));
      setCancelSubscriptionDialogOpen(false);
      loadUsers();
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: error instanceof Error ? error.message : 'Failed to cancel subscription' }));
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedUsers.length === 0) return;
    try {
      await apiService.bulkUpdatePlatformUsers(selectedUsers, action);
      dispatch(addNotification({ type: 'success', message: `Bulk action "${action}" completed` }));
      setSelectedUsers([]);
      setBulkActionMenuAnchor(null);
      loadUsers();
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: error instanceof Error ? error.message : 'Bulk action failed' }));
    }
  };

  const handleExportUsers = async (format: 'csv' | 'excel') => {
    try {
      await apiService.exportPlatformUsers(format, filters);
      dispatch(addNotification({ type: 'success', message: 'Export started' }));
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: error instanceof Error ? error.message : 'Export failed' }));
    }
  };

  // Initial load
  useEffect(() => {
    loadAnalytics();
    loadUsers(true);
    // Load authoritative subscription plans from API; fall back silently on error
    apiService.getSubscriptionPlans().then((data: any) => {
      if (data?.plans && Array.isArray(data.plans) && data.plans.length) {
        setSubscriptionPlans(data.plans);
      }
    }).catch(() => { /* keep fallback */ });
    // Country filter options. Failing silently is correct: an unavailable filter
    // list must never stop the member list itself from rendering.
    apiService.getPlatformMemberLocations().then((data) => {
      if (data?.countries?.length) setAvailableCountries(data.countries);
    }).catch(() => { /* filter simply offers nothing */ });
  }, []);

  useEffect(() => {
    if (!loading) loadUsers();
  }, [page, rowsPerPage]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAnalytics();
    loadUsers();
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const isSelected = (userId: number) => selectedUsers.includes(userId);
  const numSelected = selectedUsers.length;
  const rowCount = users.length;

  // Prepare chart data
  const planDistData = (stats?.plan_distribution || []).map(p => ({
    name: (p.plan || 'free').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value: p.count,
    fill: PLAN_COLORS[p.plan] || '#94a3b8',
  }));

  let _cumulativeMembers = 0;
  const growthData = (stats?.member_growth || []).map(g => {
    _cumulativeMembers += g.count;
    return {
      date: new Date(g.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      members: g.count,
      cumulative: _cumulativeMembers,
    };
  });

  const contentBarData = stats ? [
    { name: 'With Chapters', value: stats.content_stats.members_with_chapters },
    { name: 'With Quests', value: stats.quest_stats.members_with_quests },
    { name: 'Verified Email', value: stats.health.verified_members },
    { name: 'Vault Subs', value: stats.health.vault_subscribers },
  ] : [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Platform Member Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive analytics and management for all platform members
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExportUsers('csv')}>
            Export
          </Button>
        </Box>
      </Box>

      {/* === ANALYTICS DASHBOARD === */}
      {analyticsLoading ? (
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2, mb: 2 }} />
          <Grid container spacing={2}>
            {[1,2,3,4].map(i => (
              <Grid size={3} key={i}><Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} /></Grid>
            ))}
          </Grid>
        </Box>
      ) : stats && (
        <>
          {/* Health Banner */}
          <MemberHealthBanner
            gradient="linear-gradient(135deg, #0f3d50, #1A5F7A)"
            items={[
              { value: (stats.health.free_members + stats.health.trial_members + stats.health.active_subscribers).toLocaleString(), label: 'TOTAL MEMBERS', delta: `\u2191 +${stats.health.new_this_month} this month`, deltaColor: '#6ee7b7' },
              { value: stats.health.active_subscribers.toLocaleString(), label: 'ACTIVE SUBSCRIBERS', delta: `${stats.health.premium_members} premium`, deltaColor: '#6ee7b7' },
              { value: `$${stats.arpu.toFixed(2)}`, label: 'AVG REV / USER', delta: '$9.99 target', deltaColor: 'rgba(255,255,255,0.5)' },
              { value: stats.health.verified_members.toLocaleString(), label: 'VERIFIED MEMBERS', delta: `${stats.health.total_members > 0 ? ((stats.health.verified_members / stats.health.total_members) * 100).toFixed(0) : 0}% verified`, deltaColor: 'rgba(255,255,255,0.5)' },
              { value: stats.health.new_this_week.toLocaleString(), label: 'NEW THIS WEEK', delta: `${stats.health.new_this_month} this month`, deltaColor: '#6ee7b7' },
              { value: stats.health.vault_subscribers.toLocaleString(), label: 'VAULT SUBSCRIBERS', delta: 'Family Vault active', deltaColor: 'rgba(255,255,255,0.5)' },
            ]}
          />

          {/* At-Risk Alert */}
          <AlertBanner
            show={stats.at_risk_count > 0}
            gradient="linear-gradient(135deg, #7f1d1d, #dc2626)"
            icon={<WarningIcon />}
            title={`${stats.at_risk_count} At-Risk Members`}
            subtitle="Free/trial members registered 14+ days ago without a subscription"
            stats={[
              { value: stats.at_risk_count, label: 'At Risk' },
              { value: stats.health.free_members, label: 'Free Tier' },
            ]}
          />

          {/* KPI Cards */}
          <KpiCardGrid
            columns={6}
            cards={[
              { label: 'Free & Trial Members', value: (stats.health.free_members + stats.health.trial_members).toLocaleString(), subtitle: `${stats.health.free_members} free · ${stats.health.trial_members} trial`, topBorderColor: '#94a3b8' },
              { label: 'Standard Members', value: stats.health.basic_members.toLocaleString(), subtitle: 'Monthly/Annual standard', topBorderColor: '#34d399' },
              { label: 'Premium Members', value: stats.health.premium_members.toLocaleString(), subtitle: 'Unlimited access', topBorderColor: '#D4AC0D' },
              { label: 'Legacy Creators', value: stats.health.legacy_creator_members.toLocaleString(), subtitle: 'Legacy plan', topBorderColor: '#dc2626' },
              { label: 'Family Vault Members', value: stats.health.vault_subscribers.toLocaleString(), subtitle: 'Active vault subscriptions', topBorderColor: '#7c3aed' },
              { label: 'Avg Chapters', value: stats.content_stats.avg_chapters.toString(), subtitle: `${stats.content_stats.total_chapters.toLocaleString()} total`, topBorderColor: '#059669' },
            ]}
          />

          {/* Charts Row 1: Member Growth — Full Width */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={12}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700, mb: 2 }}>
                  Member Growth (90 Days)
                </Typography>
                <Box sx={{ height: 280, minHeight: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={growthData} margin={{ top: 4, right: 40, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="memberGrowthGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1A5F7A" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#1A5F7A" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        interval={Math.floor(growthData.length / 8)}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 10 }}
                        allowDecimals={false}
                        width={32}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 10, fill: '#10B981' }}
                        allowDecimals={false}
                        width={40}
                      />
                      <RechartsTooltip contentStyle={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }} />
                      <Legend wrapperStyle={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px' }} />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="members"
                        name="Daily Signups"
                        stroke="#1A5F7A"
                        fill="url(#memberGrowthGrad)"
                        strokeWidth={2}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="cumulative"
                        name="Cumulative Members"
                        stroke="#10B981"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Charts Row 2: Plan Distribution + Activity Funnel */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700, mb: 2 }}>
                  Plan Distribution
                </Typography>
                <Box sx={{ height: 280, minHeight: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={planDistData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        {planDistData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <ConversionFunnel
                  title="Platform Activity Funnel"
                  steps={[
                    { label: 'Registered', value: stats.health.total_members, color: '#1A5F7A' },
                    { label: 'Verified', value: stats.health.verified_members, color: '#0284c7' },
                    { label: 'Subscribed', value: stats.health.active_subscribers, color: '#059669' },
                    { label: 'Created Content', value: stats.content_stats.members_with_chapters, color: '#D4AC0D' },
                    { label: 'Quest Active', value: stats.quest_stats.members_with_quests, color: '#7c3aed' },
                  ]}
                />
              </Paper>
            </Grid>
          </Grid>

          {/* Charts Row 3: Member Engagement — Full Width */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={12}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700, mb: 2 }}>
                  Member Engagement
                </Typography>
                <Box sx={{ height: 280, minHeight: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={contentBarData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RechartsTooltip />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {contentBarData.map((entry, idx) => (
                          <Cell key={idx} fill={['#1A5F7A', '#7c3aed', '#059669', '#D4AC0D'][idx]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Charts Row 4: Why Members Joined, by Age — Full Width */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={12}>
              <JoinReasonByAgeChart />
            </Grid>
          </Grid>
        </>
      )}

      {/* === EXISTING USER TABLE & MANAGEMENT === */}
      <Divider sx={{ my: 3 }} />

      {/* Action bar — Create Member */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setCreateMemberDialogOpen(true)}
          sx={{ fontWeight: 600 }}
        >
          Create Member
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or phone..."
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="sub-filter" sx={{ bgcolor: 'background.paper', px: 0.5 }}>Subscription</InputLabel>
              <Select labelId="sub-filter" value={filters.subscription_plan} onChange={(e) => handleFilterChange('subscription_plan', e.target.value)} label="Subscription" displayEmpty>
                <MenuItem value="">All Plans</MenuItem>
                {subscriptionPlans
                  .filter(p => !p.plan_code.startsWith('vault_') && p.plan_code !== 'wedding_life_book')
                  .map(p => (
                    <MenuItem key={p.plan_code} value={p.plan_code}>
                      {p.display_name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="vault-filter" sx={{ bgcolor: 'background.paper', px: 0.5 }}>Family Vault</InputLabel>
              <Select labelId="vault-filter" value={filters.family_vault_tier} onChange={(e) => handleFilterChange('family_vault_tier', e.target.value)} label="Family Vault" displayEmpty>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="credits-filter" sx={{ bgcolor: 'background.paper', px: 0.5 }}>Credits</InputLabel>
              <Select labelId="credits-filter" value={filters.chapter_credits} onChange={(e) => handleFilterChange('chapter_credits', e.target.value)} label="Credits" displayEmpty>
                <MenuItem value="">Any Credits</MenuItem>
                <MenuItem value="1">1+ Credits</MenuItem>
                <MenuItem value="5">5+ Credits</MenuItem>
                <MenuItem value="10">10+ Credits</MenuItem>
                <MenuItem value="25">25+ Credits</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="styles-filter" sx={{ bgcolor: 'background.paper', px: 0.5 }}>Styles</InputLabel>
              <Select labelId="styles-filter" value={filters.styles_purchased} onChange={(e) => handleFilterChange('styles_purchased', e.target.value)} label="Styles" displayEmpty>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="writing">Writing</MenuItem>
                <MenuItem value="illustration">Illustration</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="age-filter" sx={{ bgcolor: 'background.paper', px: 0.5 }}>Age</InputLabel>
              <Select labelId="age-filter" value={filters.age_range} onChange={(e) => handleFilterChange('age_range', e.target.value)} label="Age" displayEmpty>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="18-25">18-25</MenuItem>
                <MenuItem value="26-35">26-35</MenuItem>
                <MenuItem value="36-45">36-45</MenuItem>
                <MenuItem value="46-55">46-55</MenuItem>
                <MenuItem value="56-65">56-65</MenuItem>
                <MenuItem value="65+">65+</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Location — country is multi-select; counts come from the member base
              itself so we never offer a country that would return nothing. */}
          <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="country-filter" sx={{ bgcolor: 'background.paper', px: 0.5 }}>Country</InputLabel>
              <Select
                labelId="country-filter"
                multiple
                value={filters.location_country}
                onChange={(e) => handleFilterChange('location_country', e.target.value as unknown as string)}
                label="Country"
                displayEmpty
                renderValue={(selected) => {
                  const list = selected as string[];
                  if (!list.length) return 'All Countries';
                  if (list.length <= 2) {
                    return list.map((c) => (c === 'UNKNOWN' ? 'Unknown' : `${flagEmoji(c)} ${c}`)).join(', ');
                  }
                  return `${list.length} countries`;
                }}
                MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
              >
                {availableCountries.map((c) => (
                  <MenuItem key={c.country_code} value={c.country_code}>
                    <Checkbox checked={filters.location_country.indexOf(c.country_code) > -1} size="small" />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>
                        {c.country_code === 'UNKNOWN'
                          ? 'Unknown'
                          : `${flagEmoji(c.country_code)} ${countryName(c.country_code)}`}
                      </span>
                      <Typography variant="caption" color="text.secondary">{c.member_count}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="confidence-filter" sx={{ bgcolor: 'background.paper', px: 0.5 }}>Confidence</InputLabel>
              <Select
                labelId="confidence-filter"
                value={filters.location_confidence}
                onChange={(e) => handleFilterChange('location_confidence', e.target.value)}
                label="Confidence"
                displayEmpty
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="high">High only</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="none">Unresolved</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="account-class-filter" sx={{ bgcolor: 'background.paper', px: 0.5 }}>Account Type</InputLabel>
              <Select
                labelId="account-class-filter"
                value={filters.account_class}
                onChange={(e) => handleFilterChange('account_class', e.target.value)}
                label="Account Type"
              >
                <MenuItem value="member">Members only</MenuItem>
                <MenuItem value="flagged_test_candidate">Flagged test candidates</MenuItem>
                <MenuItem value="confirmed_test">Confirmed test</MenuItem>
                <MenuItem value="quarantined_test">Quarantined</MenuItem>
                <MenuItem value="all">All account types</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Actions */}
      {numSelected > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {numSelected} user{numSelected > 1 ? 's' : ''} selected
            </Typography>
            <Button variant="contained" size="small" onClick={(e) => setBulkActionMenuAnchor(e.currentTarget)}>
              Bulk Actions
            </Button>
          </Box>
        </Paper>
      )}

      {/* User Table */}
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    checked={rowCount > 0 && numSelected === rowCount}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>User</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Family Vault</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Styles</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && page === 0 ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 11 }).map((_, j) => (
                      <TableCell key={j}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover selected={isSelected(user.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={isSelected(user.id)} onChange={() => handleSelectUser(user.id)} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={user.profile_picture} sx={{ width: 40, height: 40 }}>
                          {user.first_name?.charAt(0) || ''}{user.last_name?.charAt(0) || ''}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">ID: {user.id}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">{user.email}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="caption">
                            {user.phone_display}
                            {user.phone_number && (
                              <Chip label={user.phone_verified ? 'Verified' : 'Not Verified'} color={user.phone_verified ? 'success' : 'default'} size="small" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                        </Box>
                        {user.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon fontSize="small" color="action" />
                            <Typography variant="caption">{user.location}</Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <MemberLocation
                        location_country_code={user.location_country_code}
                        location_region={user.location_region}
                        location_city={user.location_city}
                        location_source={user.location_source}
                        location_confidence={user.location_confidence}
                        location_resolved_at={user.location_resolved_at}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Chip
                          label={user.has_unlimited_credits || user.subscription_status === 'active' ? 'Active' : 'Inactive'}
                          color={user.has_unlimited_credits || user.subscription_status === 'active' ? 'success' : 'default'}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {displayNameFor(user.subscription_plan, subscriptionPlans)}
                        </Typography>
                        {user.age_range && <Typography variant="caption" display="block" color="text.secondary">Age: {user.age_range}</Typography>}
                        {user.last_login_at && <Typography variant="caption" display="block" color="text.secondary">Last: {formatDate(user.last_login_at)}</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.family_vault_tier ? (
                        <Box>
                          <Chip
                            icon={<VaultIcon />}
                            label={user.family_vault_tier.charAt(0).toUpperCase() + user.family_vault_tier.slice(1)}
                            color={TIER_COLORS[`vault_${user.family_vault_tier}`] || 'default'}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          {user.family_vault_current_period_end && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              Until {formatDate(user.family_vault_current_period_end)}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Chip label="None" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {user.chapter_credits > 0 ? `${user.chapter_credits} Credits` : 'No Credits'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {user.chapter_credits === 5 ? '5 Chapter Pack' : user.chapter_credits === 10 ? '10 Chapter Pack' : user.chapter_credits === 25 ? '25 Chapter Pack' : user.chapter_credits > 0 ? 'Custom Amount' : 'No purchases'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {user.total_styles_purchased > 0 ? `${user.total_styles_purchased} Styles` : 'No Styles'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {user.purchased_writing_styles?.length || 0} Writing + {user.purchased_illustration_styles?.length || 0} Illustration
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.progress_display}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{user.quest_level_display}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{user.quest_progress}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(user.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewUser(user)}><ViewIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Edit User">
                          <IconButton size="small" onClick={() => handleEditUser(user)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={user.has_unlimited_credits || user.subscription_status === 'active' ? 'Deactivate Subscription' : 'Activate Subscription'}>
                          <IconButton
                            size="small"
                            color={user.has_unlimited_credits || user.subscription_status === 'active' ? 'warning' : 'success'}
                            sx={{
                              border: '2px solid',
                              borderColor: user.has_unlimited_credits || user.subscription_status === 'active' ? 'warning.main' : 'success.main',
                              '&:hover': { backgroundColor: user.has_unlimited_credits || user.subscription_status === 'active' ? 'warning.light' : 'success.light' }
                            }}
                            onClick={() => {
                              if (user.has_unlimited_credits || user.subscription_status === 'active') {
                                handleCancelSubscription(user);
                              } else {
                                handleActivateSubscription(user);
                              }
                            }}
                          >
                            {user.has_unlimited_credits || user.subscription_status === 'active' ? <CancelIcon fontSize="small" /> : <StarIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reset Password">
                          <IconButton size="small" onClick={() => { setSelectedUser(user); setResetPasswordDialogOpen(true); }}>
                            <PasswordIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More Actions">
                          <IconButton size="small" onClick={(e) => { setSelectedUser(user); setActionMenuAnchor(e.currentTarget); }}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Action Menu */}
      <Menu anchorEl={actionMenuAnchor} open={Boolean(actionMenuAnchor)} onClose={() => setActionMenuAnchor(null)}>
        <MuiMenuItem onClick={() => { if (selectedUser) handleViewUser(selectedUser); setActionMenuAnchor(null); }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem onClick={() => { if (selectedUser) handleEditUser(selectedUser); setActionMenuAnchor(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem onClick={() => { if (selectedUser) setResetPasswordDialogOpen(true); setActionMenuAnchor(null); }}>
          <ListItemIcon><PasswordIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Reset Password</ListItemText>
        </MuiMenuItem>
        <Divider />
        <MuiMenuItem onClick={() => {
          if (selectedUser) {
            if (selectedUser.has_unlimited_credits || selectedUser.subscription_status === 'active') handleCancelSubscription(selectedUser);
            else handleActivateSubscription(selectedUser);
          }
          setActionMenuAnchor(null);
        }}>
          <ListItemIcon>
            {selectedUser && (selectedUser.has_unlimited_credits || selectedUser.subscription_status === 'active') ? <CancelIcon fontSize="small" /> : <StarIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {selectedUser && (selectedUser.has_unlimited_credits || selectedUser.subscription_status === 'active') ? 'Deactivate Subscription' : 'Activate Subscription'}
          </ListItemText>
        </MuiMenuItem>
      </Menu>

      {/* Bulk Action Menu */}
      <Menu anchorEl={bulkActionMenuAnchor} open={Boolean(bulkActionMenuAnchor)} onClose={() => setBulkActionMenuAnchor(null)}>
        <MuiMenuItem onClick={() => handleBulkAction('activate')}>
          <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Activate Selected</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleBulkAction('deactivate')}>
          <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Deactivate Selected</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleBulkAction('reset_password')}>
          <ListItemIcon><PasswordIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Reset Passwords</ListItemText>
        </MuiMenuItem>
        <Divider />
        <MuiMenuItem onClick={() => handleExportUsers('csv')}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export Selected</ListItemText>
        </MuiMenuItem>
      </Menu>

      {/* View User Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>User Details - {selectedUser?.first_name} {selectedUser?.last_name}</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={3} sx={{ pt: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" gutterBottom>Personal Information</Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" gutterBottom><strong>Name:</strong> {selectedUser.first_name} {selectedUser.last_name}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Email:</strong> {selectedUser.email}</Typography>
                  {selectedUser.phone_number && <Typography variant="body2" gutterBottom><strong>Phone:</strong> {selectedUser.phone_number}</Typography>}
                  {/* Two different things, never merged: what the member typed
                      about themselves, and what we inferred from their signals. */}
                  {selectedUser.location && <Typography variant="body2" gutterBottom><strong>Stated Location:</strong> {selectedUser.location}</Typography>}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" component="span"><strong>Detected Location:</strong></Typography>
                    <MemberLocation
                      location_country_code={selectedUser.location_country_code}
                      location_region={selectedUser.location_region}
                      location_city={selectedUser.location_city}
                      location_source={selectedUser.location_source}
                      location_confidence={selectedUser.location_confidence}
                      location_resolved_at={selectedUser.location_resolved_at}
                    />
                  </Box>
                  {selectedUser.age_range && <Typography variant="body2" gutterBottom><strong>Age Range:</strong> {selectedUser.age_range}</Typography>}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" gutterBottom>Account Information</Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" gutterBottom><strong>User ID:</strong> {selectedUser.id}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Subscription:</strong> {selectedUser.subscription_plan}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Chapter Credits:</strong> {selectedUser.chapter_credits}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Joined:</strong> {formatDate(selectedUser.created_at)}</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => { setViewDialogOpen(false); if (selectedUser) handleEditUser(selectedUser); }}>Edit User</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit User — {selectedUser?.first_name} {selectedUser?.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage personal info, subscription, vault access, and account status
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Section 1: Personal Information */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: '12px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" color="primary" />
              Personal Information
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="First Name" value={editFormData.first_name || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, first_name: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Last Name" value={editFormData.last_name || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, last_name: e.target.value }))} />
              </Grid>
              <Grid size={12}>
                <TextField fullWidth label="Email" type="email" value={editFormData.email || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Phone Number" value={editFormData.phone_number || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, phone_number: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Location" value={editFormData.location || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, location: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField fullWidth label="Bio" multiline rows={2} value={editFormData.bio || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, bio: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Age Range</InputLabel>
                  <Select value={editFormData.age_range || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, age_range: e.target.value }))} label="Age Range">
                    <MenuItem value="">Not Set</MenuItem>
                    <MenuItem value="59-64">59-64</MenuItem>
                    <MenuItem value="65-69">65-69</MenuItem>
                    <MenuItem value="70-74">70-74</MenuItem>
                    <MenuItem value="75-79">75-79</MenuItem>
                    <MenuItem value="80+">80+</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Section 2: Subscription & Billing */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: '12px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MonetizationIcon fontSize="small" color="primary" />
              Subscription & Billing
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 5 }}>
                <FormControl fullWidth>
                  <InputLabel>Plan</InputLabel>
                  <Select value={editFormData.subscription_plan || 'free'} onChange={(e) => setEditFormData(prev => ({ ...prev, subscription_plan: e.target.value }))} label="Plan">
                    <ListSubheader sx={{ fontWeight: 700, color: 'text.primary' }}>Free / Trial</ListSubheader>
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="trial">Trial</MenuItem>
                    <ListSubheader sx={{ fontWeight: 700, color: 'text.primary' }}>Standard</ListSubheader>
                    <MenuItem value="standard_monthly">Standard Monthly</MenuItem>
                    <MenuItem value="standard_annual">Standard Annual</MenuItem>
                    <ListSubheader sx={{ fontWeight: 700, color: 'text.primary' }}>Premium</ListSubheader>
                    <MenuItem value="premium_monthly">Premium Monthly</MenuItem>
                    <MenuItem value="premium_annual">Premium Annual</MenuItem>
                    <ListSubheader sx={{ fontWeight: 700, color: 'text.primary' }}>Legacy Creator</ListSubheader>
                    <MenuItem value="legacy_monthly">Legacy Creator Monthly</MenuItem>
                    <MenuItem value="legacy_annual">Legacy Creator Annual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip
                    label={selectedUser?.subscription_status || 'inactive'}
                    color={selectedUser?.subscription_status === 'active' ? 'success' : selectedUser?.subscription_status === 'past_due' ? 'warning' : 'default'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControlLabel
                  control={<Switch checked={editFormData.has_unlimited_credits || false} onChange={(e) => setEditFormData(prev => ({ ...prev, has_unlimited_credits: e.target.checked }))} />}
                  label="Unlimited Credits"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth label="Chapter Credits" type="number" value={editFormData.chapter_credits ?? 0} onChange={(e) => setEditFormData(prev => ({ ...prev, chapter_credits: parseInt(e.target.value) || 0 }))} InputProps={{ inputProps: { min: 0 } }} />
              </Grid>
            </Grid>
          </Paper>

          {/* Section 3: Family Vault Subscription */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: '12px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon fontSize="small" color="primary" />
              Family Vault Subscription
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 5 }}>
                <FormControl fullWidth>
                  <InputLabel>Vault Tier</InputLabel>
                  <Select value={(editFormData as any).vault_subscription_tier || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, vault_subscription_tier: e.target.value } as any))} label="Vault Tier">
                    <MenuItem value="">None (revoke)</MenuItem>
                    <MenuItem value="standard">Standard — 100 GB, 7 members</MenuItem>
                    <MenuItem value="premium">Premium — 250 GB, 20 members</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth disabled={!(editFormData as any).vault_subscription_tier}>
                  <InputLabel>Billing Cycle</InputLabel>
                  <Select value={(editFormData as any).vault_subscription_cycle || 'annual'} onChange={(e) => setEditFormData(prev => ({ ...prev, vault_subscription_cycle: e.target.value } as any))} label="Billing Cycle">
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Current Status"
                  value={selectedUser?.family_vault_status || 'none'}
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={12}>
                <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                  Changes apply on Save. Setting tier to <strong>None</strong> cancels the active addon (soft-delete).
                  All vault changes are logged with <code>comp_grant=true</code>.
                </Alert>
              </Grid>
            </Grid>
          </Paper>

          {/* Section 4: Account Status */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: '12px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon fontSize="small" color="primary" />
              Account Status
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 6, md: 3 }}>
                <FormControlLabel
                  control={<Switch checked={editFormData.is_active ?? true} onChange={(e) => setEditFormData(prev => ({ ...prev, is_active: e.target.checked }))} />}
                  label="Active User"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <FormControlLabel
                  control={<Switch checked={selectedUser?.email_verified || false} disabled />}
                  label="Email Verified"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="Joined" value={selectedUser ? formatDate(selectedUser.created_at) : ''} disabled InputProps={{ readOnly: true }} size="small" />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="Last Updated" value={selectedUser?.updated_at ? formatDate(selectedUser.updated_at) : ''} disabled InputProps={{ readOnly: true }} size="small" />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="User ID"
                  value={selectedUser?.id || ''}
                  disabled
                  size="small"
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => { navigator.clipboard.writeText(String(selectedUser?.id || '')); dispatch(addNotification({ type: 'success', message: 'User ID copied' })); }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveUser} sx={{ bgcolor: '#1A5F7A', '&:hover': { bgcolor: '#0f3d50' } }}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body1">Are you sure you want to reset the password for {selectedUser?.first_name} {selectedUser?.last_name}?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>A password reset email will be sent to {selectedUser?.email}.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => { if (selectedUser) handleResetPassword(selectedUser.id); setResetPasswordDialogOpen(false); }}>Reset Password</Button>
        </DialogActions>
      </Dialog>

      {/* Activate Subscription Dialog */}
      <Dialog open={subscriptionDialogOpen} onClose={() => setSubscriptionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonetizationIcon color="primary" />
            Manage User Subscription & Credits
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>Manage subscription and credits for {selectedUser?.full_name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Current: {selectedUser?.subscription_display_text} | Credits: {selectedUser?.chapter_credits}</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><StarIcon color="primary" />Subscription Plan</Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Plan Type</InputLabel>
                  <Select value={subscriptionFormData.plan_type} onChange={(e) => setSubscriptionFormData(prev => ({ ...prev, plan_type: e.target.value }))} label="Plan Type">
                    {subscriptionPlans
                      .filter(p => !p.plan_code.startsWith('vault_') && p.plan_code !== 'wedding_life_book')
                      .map(p => (
                        <MenuItem key={p.plan_code} value={p.plan_code}>
                          {p.display_name}
                          {p.annual_price ? ` — $${p.annual_price}/yr` : p.monthly_price ? ` — $${p.monthly_price}/mo` : ''}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                  {subscriptionPlans.find(p => p.plan_code === subscriptionFormData.plan_type)?.feature_summary || '15 chapters/month included'}
                </Alert>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><MonetizationIcon color="secondary" />Chapter Credits</Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Operation</InputLabel>
                  <Select value={subscriptionFormData.creditOperation} onChange={(e) => setSubscriptionFormData(prev => ({ ...prev, creditOperation: e.target.value }))} label="Operation">
                    <MenuItem value="set">Set Credits</MenuItem>
                    <MenuItem value="add">Add Credits</MenuItem>
                    <MenuItem value="subtract">Remove Credits</MenuItem>
                  </Select>
                </FormControl>
                <TextField fullWidth label="Credit Amount" type="number" value={subscriptionFormData.credits} onChange={(e) => setSubscriptionFormData(prev => ({ ...prev, credits: parseInt(e.target.value) || 0 }))} InputProps={{ inputProps: { min: 0 } }} sx={{ mb: 2 }} />
                <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>Credits allow individual chapter purchases</Alert>
              </Paper>
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 3 }}>Both subscription and credit changes will be applied immediately.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" startIcon={<ActivateIcon />} onClick={executeActivateSubscription}>Apply Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelSubscriptionDialogOpen} onClose={() => setCancelSubscriptionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon color="warning" />
            Deactivate Subscription & Manage Credits
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>Deactivate subscription for {selectedUser?.full_name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Current: {selectedUser?.subscription_display_text} | Credits: {selectedUser?.chapter_credits}</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><CancelIcon color="warning" />Subscription Deactivation</Typography>
                <FormControlLabel control={<Switch checked={cancelFormData.immediate} onChange={(e) => setCancelFormData(prev => ({ ...prev, immediate: e.target.checked }))} />} label="Deactivate immediately" sx={{ mb: 2 }} />
                <TextField fullWidth label="Deactivation Reason" multiline rows={3} value={cancelFormData.reason} onChange={(e) => setCancelFormData(prev => ({ ...prev, reason: e.target.value }))} sx={{ mb: 2 }} />
                <Alert severity={cancelFormData.immediate ? "warning" : "info"} sx={{ fontSize: '0.875rem' }}>
                  {cancelFormData.immediate ? "Immediate deactivation will revoke unlimited access right now." : "The subscription will be deactivated at the end of the current billing period."}
                </Alert>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><MonetizationIcon color="secondary" />Credits Management</Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Operation</InputLabel>
                  <Select value={cancelFormData.creditOperation || 'set'} onChange={(e) => setCancelFormData(prev => ({ ...prev, creditOperation: e.target.value }))} label="Operation">
                    <MenuItem value="set">Set Credits</MenuItem>
                    <MenuItem value="add">Add Credits</MenuItem>
                    <MenuItem value="subtract">Remove Credits</MenuItem>
                  </Select>
                </FormControl>
                <TextField fullWidth label="Credit Amount" type="number" value={cancelFormData.credits || 0} onChange={(e) => setCancelFormData(prev => ({ ...prev, credits: parseInt(e.target.value) || 0 }))} InputProps={{ inputProps: { min: 0 } }} sx={{ mb: 2 }} />
                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>Credits can be managed independently of subscription</Alert>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelSubscriptionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" startIcon={<CancelIcon />} onClick={executeCancelSubscription}>Apply Changes</Button>
        </DialogActions>
      </Dialog>

      <CreatePlatformMemberModal
        open={createMemberDialogOpen}
        onClose={() => setCreateMemberDialogOpen(false)}
        onSuccess={() => {
          loadUsers(true);
          loadAnalytics();
        }}
        plans={subscriptionPlans}
      />
    </Box>
  );
};

export default PlatformMembersTab;
